import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { UserService } from './user.service';
import { USERROLE } from './models';
import { filter, firstValueFrom, timeout, catchError, take } from 'rxjs';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {

  constructor(
    private userService: UserService,
    private router: Router,
    private msalBroadcastService: MsalBroadcastService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    let user = this.userService.getUser();
    if (!user) {
      await firstValueFrom(
        this.msalBroadcastService.inProgress$.pipe(
          filter(status => status === InteractionStatus.None),
          take(1),
          timeout(10000),
          catchError(() => of(InteractionStatus.None))
        )
      );

      try {
        user = await this.userService.getUserNameFromSession();
      } catch {
        // The app bootstrap may still be resolving the cached session.
      }
    }

    if (!user) {
      // Guard fired before app.component resolved the user from MSAL auth callbacks.
      // Wait up to 10s for userSubject to emit — it fires once app.component calls setUser().
      user = await firstValueFrom(
        this.userService.userSubject.pipe(
          filter(u => u !== undefined),
          timeout(10000),
          catchError(() => of(undefined))
        )
      ) ?? undefined;
    }

    if (!user || user.role === USERROLE.FA) {
      return this.router.parseUrl('/unauthorized');
    }

    // Route-specific restriction, e.g. data: { allowedRoles: [USERROLE.SUPERVISION] }
    const allowedRoles = route.data?.['allowedRoles'] as USERROLE[] | undefined;
    if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      return this.router.parseUrl('/unauthorized');
    }

    return true;
  }
}

